import React, { useEffect, useState, useRef } from 'react';

import {
  Paper,
  Box,
  Typography,
  MaterialUICore,
  TextArea,
} from '@iclinic/design-system';
import useStyles from './PatientHistoryCard.style';

const { Radio, RadioGroup, FormControlLabel, Collapse } = MaterialUICore;

interface PatientHistoryCardProps {
  response?: boolean;
  observation?: string;
  title: string;
  onChange: (response: boolean, observation?: string) => void;
  'data-testid'?: string;
  onTextChangeImmediate?: (observation: string) => void;
}

function PatientHistoryCard({
  response,
  observation,
  title,
  onChange,
  onTextChangeImmediate,
  'data-testid': testId,
}: PatientHistoryCardProps): JSX.Element {
  const [collapse, setCollapse] = useState(!!response);
  const [renderStateTextArea, setRenderStateTextArea] = useState(!!response);
  const [obsText, setObsText] = useState(observation);
  const timeoutRef: any = useRef();

  const classes = useStyles({ response });

  useEffect(() => {
    setCollapse(!!response);
    if (response) {
      setRenderStateTextArea(true);
    }
  }, [response]);

  const handleChangeCollapse = () => {
    if (!response) return;

    setCollapse((prev) => !prev);

    if (collapse) {
      setTimeout(() => {
        setRenderStateTextArea(false);
      }, 300);
    } else {
      setRenderStateTextArea(true);
    }
  };

  const delayedHandleTextArea = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.currentTarget;

    if (onTextChangeImmediate) onTextChangeImmediate(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setObsText(value);

    timeoutRef.current = setTimeout(() => {
      onChange(!!response, value);
    }, 500);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    clearTimeout(timeoutRef.current);
    setObsText(event.currentTarget.value);
  };

  return (
    <Box className={classes.container}>
      <Paper className={classes.card} variant="outlined">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            data-testid="titleHistoricRecord"
            className={classes.title}
            variant="body2"
            bold
            onClick={handleChangeCollapse}
          >
            {title}
          </Typography>
          <Box>
            <RadioGroup
              row
              aria-label="choiceActive"
              name="choiceActive"
              value={response?.toString() || ''}
              onChange={(event) => {
                const value = event.currentTarget.value === 'true';
                onChange(value);
              }}
            >
              <FormControlLabel
                value="false"
                className={classes.responseLabel}
                control={
                  <Radio
                    size="small"
                    color="primary"
                    inputProps={{ 'data-testid': `${testId}-false` }}
                  />
                }
                label={<span className={classes.responseLabel}>Não</span>}
              />
              <FormControlLabel
                className={classes.formLabel}
                value="true"
                control={
                  <Radio
                    size="small"
                    color="primary"
                    inputProps={{ 'data-testid': `${testId}-true` }}
                  />
                }
                label={<span className={classes.responseLabel}>Sim</span>}
              />
            </RadioGroup>
          </Box>
        </Box>
        <Collapse in={collapse} data-testid="collapseHistoryCard">
          {renderStateTextArea && (
            <Box>
              <TextArea
                fullWidth
                label="Observação"
                placeholder="Digite aqui"
                onChange={delayedHandleTextArea}
                onBlur={handleBlur}
                value={obsText}
                data-testid="iputTextAreaHistoricRecord"
                InputLabelProps={{ htmlFor: `${testId}-observation` }}
                inputProps={{ id: `${testId}-observation` }}
              />
            </Box>
          )}
        </Collapse>
      </Paper>
    </Box>
  );
}

export default PatientHistoryCard;
